import { useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Sun, Moon, LogIn, ChevronLeft } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { useTheme } from '@context/ThemeContext';
import Logo from '@components/ui/Logo';
import { AuthGradientBackground } from '@components/auth/AuthGradientBackground';
import { AuthWelcomeAside } from '@components/auth/AuthWelcomeAside';
import { GoogleSignInButton } from '@components/auth/GoogleSignInButton';
import {
  authDividerLabel,
  authDividerLine,
  authFooterMuted,
  authIconButton,
  authInput,
  authPageSubtitle,
  authPageTitle,
  authTextLink,
  authInlineLink,
} from '@components/auth/authFormClasses';
import toast from 'react-hot-toast';
import { trackLogin } from '@utils/analytics';
import { resolveAuthChromeBack } from '@utils/authEntryPaths';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, googleLoginWithCode } = useAuth();
  const { dark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const fromLocation = location.state?.from;
  const fromPath = fromLocation?.pathname || '/feed';
  const from = `${fromPath}${fromLocation?.search || ''}${fromLocation?.hash || ''}`;
  const welcomeState = location.state;

  const handleAuthChromeBack = useCallback(() => {
    const r = resolveAuthChromeBack(location.state);
    if (r.mode === 'to') {
      navigate(r.to, { state: r.state });
      return;
    }
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/');
  }, [location.state, navigate]);

  const handleGoogleSuccess = useCallback(
    async (code) => {
      setGoogleLoading(true);
      try {
        const data = await googleLoginWithCode(code);
        trackLogin('google');
        toast.success('Welcome back!');
        if (!data.user.onboardingComplete) {
          navigate('/onboarding', { replace: true, state: location.state });
        } else {
          navigate(from, { replace: true });
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Google sign-in failed');
      } finally {
        setGoogleLoading(false);
      }
    },
    [googleLoginWithCode, navigate, from, location.state],
  );

  const handleGoogleError = useCallback((err) => {
    if (err?.type === 'popup_closed') return; // user dismissed; stay silent
    toast.error('Google sign-in failed');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      trackLogin('email');
      toast.success('Welcome back!');
      if (!data.user.onboardingComplete) {
        navigate('/onboarding', { replace: true, state: location.state });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const formCard = (
    <div className="w-full min-w-0 max-w-md">
      <div className="mb-8 text-center md:text-left">
        <h2 className={authPageTitle}>Welcome back</h2>
        <p className={authPageSubtitle}>Ready to continue finding vibes near you?</p>
      </div>

      <GoogleSignInButton
        onSuccess={handleGoogleSuccess}
        onError={handleGoogleError}
        loading={googleLoading}
        disabled={loading}
        label="Continue with Google"
      />

      <div className="my-6 flex items-center gap-3">
        <div className={authDividerLine} />
        <span className={authDividerLabel}>OR</span>
        <div className={authDividerLine} />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={authInput}
          placeholder="Enter email"
          required
          autoComplete="email"
        />

        <div className="relative">
          <input
            type={showPw ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`${authInput} pr-12`}
            placeholder="Enter password"
            required
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={() => setShowPw(!showPw)}
            className={`absolute right-3.5 top-1/2 -translate-y-1/2 ${authIconButton}`}
            aria-label={showPw ? 'Hide password' : 'Show password'}
          >
            {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <div className="flex items-center justify-end pt-0.5">
          <button
            type="button"
            className={authInlineLink}
          >
            Forgot password?
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-sky-500 via-brand-500 to-violet-600 py-3.5 text-sm font-bold text-white shadow-lg shadow-brand-500/25 transition-all duration-300 hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              Signing in...
            </span>
          ) : (
            <>
              <LogIn size={16} />
              Sign in
            </>
          )}
        </button>
      </form>

      <p className={`mt-8 text-center ${authFooterMuted}`}>
        Don&apos;t have an account?{' '}
        <Link to="/register" state={welcomeState} className={authTextLink}>
          Sign up
        </Link>
      </p>
    </div>
  );

  return (
    <div className="flex min-h-screen min-w-0 flex-col bg-white dark:bg-surface-950 md:flex-row md:bg-surface-100 md:dark:bg-surface-950">
      <AuthWelcomeAside page="login" />

      {/* Mobile top */}
      <div className="relative flex min-h-[30vh] shrink-0 flex-col md:hidden">
        <AuthGradientBackground className="min-h-[30vh] flex-1 rounded-b-none">
          <div className="relative z-10 flex items-center justify-between px-4 pt-[max(0.5rem,env(safe-area-inset-top))]">
            <button
              type="button"
              onClick={handleAuthChromeBack}
              className="inline-flex items-center gap-0.5 text-sm font-semibold text-white/95 transition-colors hover:text-white"
            >
              <ChevronLeft size={18} strokeWidth={2.5} />
              Back
            </button>
            <button type="button" onClick={toggleTheme} className="theme-toggle !bg-white/15 !text-white hover:!bg-white/25" aria-label="Toggle theme">
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </AuthGradientBackground>
      </div>

      {/* Form column */}
      <div className="relative z-10 -mt-10 flex min-w-0 flex-1 flex-col rounded-t-[2rem] bg-white px-5 pb-10 pt-8 shadow-[0_-8px_40px_rgba(15,23,42,0.06)] ring-1 ring-black/[0.03] dark:bg-surface-950 dark:shadow-[0_-12px_48px_rgba(0,0,0,0.55)] dark:ring-white/[0.06] md:mt-0 md:justify-center md:rounded-none md:bg-surface-100 md:px-10 md:pb-12 md:pt-10 md:shadow-none md:ring-0 md:dark:bg-surface-950 safe-bottom">
        <div className="mb-6 hidden items-center justify-between md:flex">
          <Logo size="sm" to="/" />
          <button type="button" onClick={toggleTheme} className="theme-toggle" aria-label="Toggle theme">
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-start md:max-w-md md:justify-center">{formCard}</div>
      </div>
    </div>
  );
};

export default LoginPage;
